import { Link } from 'react-router-dom';
import { TRAINING_STAGE_LABEL } from '@allemnii-pulse/shared';
import { Card } from '../../components/ui/Card.js';
import { useTrainingSessions, useTrainingSources, useTrainingState } from './useTraining.js';

function StepCard({
  title,
  to,
  done,
  started,
  detail,
}: {
  title: string;
  to: string;
  done: boolean;
  started: boolean;
  detail: string;
}) {
  return (
    <Link to={to}>
      <Card
        className={`flex flex-col gap-2 p-4 hover:border-[var(--ink-400)] ${done ? 'border-[color-mix(in_srgb,var(--success)_35%,var(--border))]' : ''}`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
              done
                ? 'bg-success text-white'
                : started
                  ? 'bg-accent-soft text-accent-soft-fg'
                  : 'border border-border text-muted'
            }`}
          >
            {done ? '✓' : started ? '…' : ''}
          </span>
          <span className="font-medium">{title}</span>
        </div>
        <p className="text-xs text-muted">{detail}</p>
      </Card>
    </Link>
  );
}

/** The project overview's stage cards for a training project — Sources → Sessions → Slides in
 * place of PRD → TDD → Tasks. */
export function TrainingOverviewCards({ projectId }: { projectId: string }) {
  const { data: state } = useTrainingState(projectId);
  const { data: sources } = useTrainingSources(projectId);
  const { data: sessions } = useTrainingSessions(projectId);
  const ready = sessions.filter((s) => s.status === 'ready').length;
  const stale = sessions.filter((s) => s.stale).length;
  const confirmed = !!state?.sessionsConfirmedAt;

  return (
    <div className="flex flex-col gap-2">
      {state && (
        <p className="text-xs text-muted">
          Current step:{' '}
          <span className="font-medium text-text">{TRAINING_STAGE_LABEL[state.stage]}</span>
        </p>
      )}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
        <StepCard
          title="Sources"
          to={`/projects/${projectId}/sources`}
          done={!!state?.analyzedAt && !(state.questions.length > 0 && !state.questionsAnsweredAt)}
          started={sources.length > 0}
          detail={
            sources.length === 0
              ? 'No files yet'
              : `${sources.length} file${sources.length === 1 ? '' : 's'}${state?.analyzedAt ? ', analysed' : ', not analysed yet'}`
          }
        />
        <StepCard
          title="Sessions"
          to={`/projects/${projectId}/sessions`}
          done={confirmed}
          started={sessions.length > 0}
          detail={
            sessions.length === 0
              ? 'Not planned yet'
              : `${sessions.length} session${sessions.length === 1 ? '' : 's'}${confirmed ? ', confirmed' : ', awaiting confirmation'}`
          }
        />
        <StepCard
          title="Slides"
          to={`/projects/${projectId}/sessions`}
          done={sessions.length > 0 && ready === sessions.length && stale === 0}
          started={ready > 0}
          detail={
            sessions.length === 0
              ? 'Plan the sessions first'
              : `${ready} of ${sessions.length} ready${stale ? ` · ${stale} need${stale === 1 ? 's' : ''} review` : ''}`
          }
        />
      </div>
    </div>
  );
}
